import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { userAPI } from '../utils/api';
import Avatar from './Avatar';
import Loading from './Loading';
import { useLanguage } from '../contexts/LanguageContext';
import { supabase } from '../config/supabase';

interface ProfileFormState {
  displayName: string;
  avatarUrl: string;
  bio: string;
  location: string;
  websiteUrl: string;
}

const Profile: React.FC = () => {
  const { user, tokens, reloadUser, isLoading } = useAuth();
  const { t } = useLanguage();
  const [form, setForm] = useState<ProfileFormState>({
    displayName: user?.displayName ?? '',
    avatarUrl: user?.avatarUrl ?? '',
    bio: user?.bio ?? '',
    location: user?.location ?? '',
    websiteUrl: user?.websiteUrl ?? '',
  });
  const [email, setEmail] = useState(user?.email ?? '');
  const [profileLoading, setProfileLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [stats, setStats] = useState<{ totalServers: number; activeServers: number; averageServerRating: number; totalComments: number; memberSince: string } | null>(null);

  useEffect(() => {
    if (!user) return;
    setForm({
      displayName: user.displayName ?? '',
      avatarUrl: user.avatarUrl ?? '',
      bio: user.bio ?? '',
      location: user.location ?? '',
      websiteUrl: user.websiteUrl ?? '',
    });
    setEmail(user.email);
  }, [user]);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!tokens?.accessToken) {
        setProfileLoading(false);
        return;
      }

      try {
        const response = await userAPI.getProfile();
        const { user: profileUser, stats: profileStats } = response;
        setForm({
          displayName: profileUser.displayName ?? '',
          avatarUrl: profileUser.avatarUrl ?? '',
          bio: profileUser.bio ?? '',
          location: profileUser.location ?? '',
          websiteUrl: profileUser.websiteUrl ?? '',
        });
        setEmail(profileUser.email);
        setStats(profileStats);
      } catch (err) {
        console.error('Failed to load profile', err);
        // Only show error if we don't have any user data to display
        if (!user) {
          setError('Unable to load profile details. Please try again later.');
        }
      } finally {
        setProfileLoading(false);
      }
    };

    fetchProfile();
  }, [tokens?.accessToken, user]);

  const handleInputChange = (key: keyof ProfileFormState) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { value } = event.target;
    setForm((prev: ProfileFormState) => ({ ...prev, [key]: value }));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccessMessage('');

    if (!tokens?.accessToken) {
      setError(t('profile.login_required'));
      setSaving(false);
      return;
    }

    try {
      const payload = {
        displayName: form.displayName,
        avatarUrl: form.avatarUrl,
        bio: form.bio,
        location: form.location,
        websiteUrl: form.websiteUrl || null,
      };

      const response = await userAPI.updateProfile(payload);
      setStats(response.stats);
      setSuccessMessage(t('profile.success_update'));
      await reloadUser();
    } catch (err) {
      console.error('Profile update error:', err);
      const serverMessage = (err as Error & { details?: { message?: string } })?.details?.message;
      setError(serverMessage || t('profile.error_update'));
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length === 0) return;
    const file = event.target.files[0];
    const fileExt = file.name.split('.').pop();
    const fileName = `${user?.id}-${Date.now()}.${fileExt}`;
    const filePath = `${fileName}`;

    setUploading(true);
    try {
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data } = supabase.storage.from('avatars').getPublicUrl(filePath);

      setForm(prev => ({ ...prev, avatarUrl: data.publicUrl }));

      const payload = {
        avatarUrl: data.publicUrl,
      };
      await userAPI.updateProfile(payload);
      await reloadUser();

      setSuccessMessage(t('profile.success_update'));
    } catch (error) {
      console.error('Error uploading avatar:', error);
      setError('Error uploading avatar. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  if (isLoading || profileLoading) {
    return <Loading message={t('common.loading')} />;
  }

  if (!user) {
    return (
      <div className="container mx-auto py-8 text-center">
        <p>{t('profile.login_required')}</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-10 px-4">
      <h1 className="text-4xl font-bold mb-8 text-center bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
        {t('profile.title')}
      </h1>

      {error && (
        <div className="bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-200 p-4 rounded-xl mb-6 border border-red-300 dark:border-red-800 max-w-2xl mx-auto">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-200 p-4 rounded-xl mb-6 border border-green-300 dark:border-green-700 max-w-2xl mx-auto">
          {successMessage}
        </div>
      )}

      <div className="bg-white dark:bg-gradient-to-br dark:from-gray-800 dark:to-gray-900 rounded-2xl shadow-xl dark:shadow-2xl p-8 max-w-3xl mx-auto border border-gray-200 dark:border-gray-700">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-1 text-center">
            <label className="block text-gray-700 dark:text-gray-300 mb-4 font-medium">{t('profile.avatar')}</label>
            <div className="flex flex-col items-center">
              <Avatar src={form.avatarUrl} alt={form.displayName || user.username} size="xl" />
              <label className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-5 py-2 rounded-full font-semibold transition-all duration-300 transform hover:scale-105 mt-4 shadow-lg cursor-pointer">
                {uploading ? 'Uploading...' : t('profile.change_avatar')}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarUpload}
                  className="hidden"
                  disabled={uploading}
                />
              </label>
            </div>
          </div>

          <div className="lg:col-span-2">
            <form onSubmit={handleSave}>
              <div className="mb-6">
                <label htmlFor="displayName" className="block text-gray-700 dark:text-gray-300 mb-2 font-medium">{t('profile.display_name')}</label>
                <input
                  type="text"
                  id="displayName"
                  value={form.displayName}
                  onChange={handleInputChange('displayName')}
                  className="w-full px-4 py-3 bg-gray-100 dark:bg-gray-700/50 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 text-gray-900 dark:text-white"
                />
              </div>

              <div className="mb-6">
                <label htmlFor="email" className="block text-gray-700 dark:text-gray-300 mb-2 font-medium">{t('profile.email')}</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  readOnly
                  className="w-full px-4 py-3 bg-gray-100 dark:bg-gray-700/50 border border-gray-300 dark:border-gray-600 rounded-xl opacity-75 cursor-not-allowed text-gray-900 dark:text-white"
                />
              </div>

              <div className="mb-6">
                <label htmlFor="bio" className="block text-gray-700 dark:text-gray-300 mb-2 font-medium">{t('profile.bio')}</label>
                <textarea
                  id="bio"
                  value={form.bio}
                  onChange={handleInputChange('bio')}
                  rows={4}
                  className="w-full px-4 py-3 bg-gray-100 dark:bg-gray-700/50 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 text-gray-900 dark:text-white"
                  placeholder={t('profile.bio_placeholder')}
                />
              </div>



              <button
                type="submit"
                disabled={saving}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-bold py-3 px-4 rounded-xl transition-all duration-300 transform hover:scale-[1.02] shadow-lg disabled:opacity-50 disabled:transform-none"
              >
                {saving ? t('profile.saving') : t('profile.save')}
              </button>
            </form>
          </div>
        </div>

        {stats && (
          <div className="mt-10 grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-gray-100 dark:bg-gray-900/60 border border-gray-200 dark:border-gray-700 rounded-xl p-5 text-center">
              <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">{stats.totalServers}</div>
              <div className="text-gray-600 dark:text-gray-400 text-sm mt-1">{t('profile.stats.servers_created')}</div>
            </div>
            <div className="bg-gray-100 dark:bg-gray-900/60 border border-gray-200 dark:border-gray-700 rounded-xl p-5 text-center">
              <div className="text-3xl font-bold text-green-600 dark:text-green-400">{stats.activeServers}</div>
              <div className="text-gray-600 dark:text-gray-400 text-sm mt-1">{t('profile.stats.active_servers')}</div>
            </div>
            <div className="bg-gray-100 dark:bg-gray-900/60 border border-gray-200 dark:border-gray-700 rounded-xl p-5 text-center">
              <div className="text-3xl font-bold text-purple-600 dark:text-purple-400">{stats.averageServerRating.toFixed(1)}</div>
              <div className="text-gray-600 dark:text-gray-400 text-sm mt-1">{t('profile.stats.avg_rating')}</div>
            </div>
            <div className="bg-gray-100 dark:bg-gray-900/60 border border-gray-200 dark:border-gray-700 rounded-xl p-5 text-center">
              <div className="text-3xl font-bold text-amber-600 dark:text-amber-400">{stats.totalComments}</div>
              <div className="text-gray-600 dark:text-gray-400 text-sm mt-1">{t('profile.stats.comments')}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;